// src/commun/UserGuide.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const OVERLAY_ID = 'ug-overlay-root';

function ensureOverlayRoot() {
  let el = document.getElementById(OVERLAY_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = OVERLAY_ID;
    document.body.appendChild(el);
  }
  return el;
}

function waitForTarget(selector, timeout = 4000, interval = 100) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      if (Date.now() - start >= timeout) return resolve(null);
      setTimeout(tick, interval);
    };
    tick();
  });
}

const defaultButtons = {
  back: 'Précédent',
  next: 'Suivant',
  finish: 'Terminer',
  skip: 'Passer',
};

export default function UserGuide({
  stepsKey = 'defaultGuide',
  steps = [],
  autoStart = true,
  delay = 300,
  onlyOnce = true,
  force = false,
  seenFromDB = false,
  buttons = defaultButtons,
  onFinish,
}) {
  const [running, setRunning] = useState(false);
  const [index, setIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [content, setContent] = useState('');
  const overlayRoot = useMemo(ensureOverlayRoot, []);
  const highlightRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const maybeStart = async () => {
      if (!steps.length) return;
      const alreadySeen = seenFromDB;
      if (onlyOnce && !force && alreadySeen) return;

      await new Promise((r) => setTimeout(r, delay));
      if (!mounted) return;

      setIndex(0);
      setRunning(true);
    };

    if (autoStart) maybeStart();

    return () => {
      mounted = false;
    };
  }, [autoStart, delay, force, onlyOnce, steps.length, stepsKey, seenFromDB]);

  useEffect(() => {
    if (!running || index >= steps.length) return;

    let cancelled = false;

    const runStep = async () => {
      const step = steps[index];
      const el = await waitForTarget(step.target);
      if (cancelled) return;

      if (!el) {
        handleNext();
        return;
      }

      removeHighlight();
      el.classList.add('ug-highlight');
      highlightRef.current = el;

      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
      setContent(step.content || '');
      highlightTarget(step.target);

      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };

    runStep();

    const onScrollOrResize = () => {
      if (!running) return;
      const step = steps[index];
      const el = document.querySelector(step.target);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
      highlightTarget(step.target); // ✅ keep overlay cutout in sync
    };


    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [running, index, steps]);

  useEffect(() => {
    return () => removeHighlight();
  }, []);

  function removeHighlight() {
    if (highlightRef.current) {
      highlightRef.current.classList.remove('ug-highlight');
      highlightRef.current = null;
    }
  }

  function finish(seen = true) {
    removeHighlight();
    setRunning(false);
    setIndex(0);
    setTargetRect(null);

    if (typeof onFinish === 'function') {
      onFinish(seen);
    }
  }

  function handleNext() {
    if (index + 1 < steps.length) {
      setIndex((i) => i + 1);
    } else {
      finish(true);
    }
  }

  function handleBack() {
    if (index > 0) setIndex((i) => i - 1);
  }

  function highlightTarget(targetSelector) {
    const overlay = document.querySelector('.ug-overlay');
    const targetEl = document.querySelector(targetSelector);

    if (targetEl && overlay) {
      const rect = targetEl.getBoundingClientRect();
      const padding = 8;

      overlay.style.clipPath = `
        polygon(
          0% 0%,
          100% 0%,
          100% 100%,
          0% 100%,
          0% ${rect.top - padding}px,
          ${rect.left - padding}px ${rect.top - padding}px,
          ${rect.left - padding}px ${rect.bottom + padding}px,
          ${rect.right + padding}px ${rect.bottom + padding}px,
          ${rect.right + padding}px ${rect.top - padding}px,
          0% ${rect.top - padding}px
        )
      `;
    }
  }

  if (!running || !targetRect) return null;

  return createPortal(
    <>
      <div className="ug-overlay" />

      <div
        className="ug-spotlight"
        style={{
          position: 'fixed', // ✅ fixed so it follows viewport
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
        }}
      />

      <div
        className="ug-tooltip"
        style={{
          position: 'fixed', // ✅ also fixed so tooltip follows viewport
          top: targetRect.top + targetRect.height + 12,
          left: Math.max(12, targetRect.left),
        }}
      >
        <div className="ug-content">{content}</div>
        <div className="ug-actions">
          <div style={{ flex: 1 }} />
          {index > 0 && (
            <button className="ug-btn ug-btn-ghost" onClick={handleBack}>
              {buttons.back}
            </button>
          )}
          <button className="ug-btn ug-btn-primary" onClick={handleNext}>
            {index + 1 < steps.length ? buttons.next : buttons.finish}
          </button>
        </div>
      </div>
    </>,
    overlayRoot
  );
}
